<template>
  <v-fade-transition mode="out-in">
    <v-app :class="{ 'with-fab': showFloatingButtons }">
      <custom-snackbar />

      <app-top-bar :logged-in="!!(user && user.id)" />

      <component :is="layout" />

      <transition name="fadeLeft">
        <incoming-call
          v-if="showIncomingCall"
          v-model="showIncomingCall"
          :call-event="callEvent"
          :show-button-order="showButtonOrder"
        />
      </transition>

      <transition name="fadeLeft">
        <incoming-request
          v-if="showIncomingRequest"
          :transaction="transactionData"
          @open-request="showTransactionModal = true"
          @close="showIncomingRequest = false"
        />
      </transition>

      <transaction-modal
        :show.sync="showTransactionModal"
        :transaction="transactionData"
      />

      <floating-buttons v-show="showFloatingButtons" />
      <global-confirm-dialog />
      <connection-agreement-dialog />
      <kkm-server-proxy />
      <!-- Виджет живёт при любом филиале: вне VUE_APP_SUPPORT_BRANCH_IDS он сам ничего не рендерит и шлёт
           Electron-оболочке пустую сессию (support/enabled, ВРЕМЕННО). Форма - только где поддержка есть. -->
      <support-widget v-if="user && user.id" />
      <support-form-dialog v-if="user && user.id && supportEnabled" />
    </v-app>
  </v-fade-transition>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'

  // Components
  import IncomingCall from '@/components/incoming-call/IncomingCall.vue'
  import IncomingRequest from '@/components/incoming-request/IncomingRequest'
  import CustomSnackbar from '@/components/snackbar/snackbar'
  import TransactionModal from './components/transaction-modal/TransactionModal'
  import FloatingButtons from './components/ui/FloatingButtons.vue'
  import SupportWidget from '@/components/support/SupportWidget.vue'
  import SupportFormDialog from '@/components/support/form/SupportFormDialog.vue'

  // Layouts
  import KitchenLayout from './layouts/kitchen/Kitchen'
  import OrderLayout from './layouts/order/Order'
  import EditOrderLayout from './layouts/edit-order/EditOrder'
  import StatisticDefaultLayout from '@/layouts/statistic/StatisticDefaultLayout'
  import StatisticCleanLayout from '@/layouts/statistic/StatisticCleanLayout'

  import '@/styles/overrides.sass'
  import colors from 'vuetify/lib/util/colors'
  import { DateTime, Settings } from 'luxon'

  // Mixins
  import mixinSnackbarMessage from '@/mixins/snackbar-message'

  // WebSockets
  // import Echo from 'laravel-echo'
  import _ from 'lodash'
  import { websocketService } from '@/services/WebsocketService'
  import { refreshService } from '@/services/refresh'
  import PhoneCallStatus from '@/models/enums/PhoneCallStatus'
  import GlobalConfirmDialog from '@/components/ui/dialogs/GlobalConfirmDialog.vue'
  import UpdateAppMassage from '@/components/UpdateAppMassage.vue'
  import ConnectionAgreementDialog from './components/connection-agreement/ConnectionAgreementDialog.vue'
  import AppTopBar from '@/components/top-bar/AppTopBar.vue'
  import permissions from './mixins/permissions'
  import CRMPermission from './models/enums/permissions/CRMPermission'
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import KkmServerProxy from '@/components/system/kkmServerProxy.vue'
  import { CRM_BILLING_BLOCKING_CHANGED, ORDER_CANCELED, PREORDER_AUTO_PREPARED, UPDATE_ORDER } from '@/constants/websocket-events'
  import { PAYMENT_ACCOUNT_REFRESH_MS } from '@/constants/billing'
  import { getFilialChannel, getOrdersChannel } from '@/util/websocket-channels'
  import { isRunningAsPwa } from '@/util/pwa'
  import OrderStatus from '@/models/enums/OrderStatus'
  import Role from '@/models/enums/Roles'
  import { createSound } from '@/util/sound'
  import { findMissedOrders } from '@/util/missed-orders'
  import { getFilialSettingValue } from '@/util/filialHelper'
  import { FALLBACK_NEW_ORDER_SOUND_URL, NO_SOUND } from '@/constants/sounds'
  import SettingKey from '@/models/enums/SettingKey'
  window.io = require('socket.io-client')

  // const token = '********'

  /* const echo = new Echo({
  broadcaster: 'socket.io',
  host: 'api.sushifox.ru:6001', // значение должно быть равным authHost из конфига + порт,
  transports: ['websocket'],
  auth: {
    headers: {
      Authorization: 'Bearer ' + token,
    },
  },
}) */

  const defaultLayout = 'default-view'

  export default {
    name: 'App',
    components: {
      KkmServerProxy,
      LargeBtn,
      UpdateAppMassage,
      GlobalConfirmDialog,
      OrderLayout,
      EditOrderLayout,
      KitchenLayout,
      IncomingCall,
      IncomingRequest,
      DefaultView: () => import('./layouts/default/View'),
      StatisticDefaultLayout,
      StatisticCleanLayout,
      CustomSnackbar,
      TransactionModal,
      FloatingButtons,
      ConnectionAgreementDialog,
      AppTopBar,
      SupportWidget,
      SupportFormDialog,
    },
    mixins: [mixinSnackbarMessage, permissions],
    provide () {
      return {
        announceMissedNewOrders: this.announceMissedNewOrders,
      }
    },
    metaInfo () {
      const route = this.$route || {}
      const meta = route.meta || {}
      const breadcrumbLabel = meta.breadcrumb && meta.breadcrumb.label
      const pageName = this.storePageTitle || meta.pageTitle || breadcrumbLabel || route.name || ''
      // PWA: только имя страницы; браузер: «Имя страницы | F.A.K.T CRM».
      const title = !pageName
        ? 'F.A.K.T CRM'
        : this.isPwa
          ? pageName
          : `${pageName} | F.A.K.T CRM`
      return {
        title,
        htmlAttrs: { lang: 'ru' },
        meta: [
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ],
      }
    },
    primary: {
      base: colors.orange.base,
      darken: colors.orange.darken2,
    },
    data () {
      return {
        isPwa: isRunningAsPwa(),
        showIncomingCall: false,
        showIncomingRequest: false,
        showReplenishmentModal: false,
        showButtonOrder: false,
        showTransactionModal: false,
        callEvent: {},
        transactionData: {},
        commandId: null,
        newOrderSound: null,
        // Заказы, по которым уже прозвучал звук: пересинхронизация не озвучит их второй раз
        announcedNewOrderIds: new Set(),
        orderSoundsReady: false,
        preorderAutoPrepareSound: null,
        paymentAccountTimer: null,
        listenersUserId: null,
      }
    },
    computed: {
      ...mapState({
        user: (state) => state.auth.user,
        showFloatingButtons: (state) => state.app.showFloatingButtons,
        storePageTitle: state => state.app.pageTitle,
        soundPresetsLoaded: (state) => state.sounds.loaded,
      }),
      ...mapGetters({
        globalFilialId: 'branches/getGlobalFilialId',
        globalFilial: 'appSettings/getGlobalFilial',
        localStorageBranch: 'branches/getLocalStorageBranch',
        getSelectedDate: 'orders/getSelectedDate',
        allFilials: 'branches/getBranches',
        isCallCenter: 'branches/getGlobalFilialIsCallCenter',
        supportEnabled: 'support/enabled',
      }),
      layout () {
        return this.$route.meta.layout || defaultLayout
      },
    },
    watch: {
      globalFilial () {
        this.initOrderSounds()
      },

      user: {
        handler () {
          if (this.user && this.user.id) {
            this.startConnectionRecovery()
            this.updateListeners()
          }
        },
        deep: true,
      },

      getSelectedDate () {
        this.$store.dispatch('indicators/fetchFilialDayMetrics', { clear: true })
      },

      localStorageBranch () {
        this.$store.dispatch('appSettings/loadGlobalFilialInfo')
        this.$store.dispatch('indicators/fetchFilialDayMetrics', { clear: true })
        this.initStopListBadge()

        // У филиалов разных организаций счета разные: старый баланс и блокировка
        // не должны пережить переключение
        this.$store.commit('billing/resetPaymentAccount')
        this.loadPaymentAccount()
      },
    },
    beforeCreate () {
      console.log('====', this.user)
    /* if (this.user && this.user.id) {
      this.$store.dispatch('auth/fetchUser')
    } */
    },
    async mounted () {
      if (this.user && this.user.id) {
        // this.updatePerformanceIndicators({ clear: true })
        if (this.localStorageBranch) {
          this.$store.dispatch('indicators/fetchFilialDayMetrics', { clear: true })
          this.$store.dispatch('reviews/fetchFinalRating')
          this.initStopListBadge()
        }

        // this.$store.dispatch('admin/fetchAdminShortInfo')
        this.startConnectionRecovery()
        this.updateListeners()
      }
    },
    async created () {
      window.addEventListener('online', this.onNetworkChange)
      window.addEventListener('offline', this.onNetworkChange)

      this.$store.dispatch('updateApp/initEvents')

      Settings.defaultZone = 'Europe/Moscow'
      if (this.user && this.user.id) {
        await this.$store.dispatch('auth/fetchUser')
        await this.$store.dispatch('branches/fetchBranches')
        this.setGlobalFilial()

        await this.$store.dispatch('appSettings/loadGlobalFilialInfo')

        // Блокировка и предупреждение перекрывают любую страницу, поэтому счёт
        // грузим на уровне приложения
        this.loadPaymentAccount()
        this.startPaymentAccountRefresh()

        if (this.userCan(CRMPermission.AcceptConnectionAgreement)) {
          await this.$store.dispatch(
            'connectionAgreement/fetchUnconnectedOrganizations',
          )
        }
      }
    },
    beforeDestroy () {
      window.removeEventListener('online', this.onNetworkChange)
      window.removeEventListener('offline', this.onNetworkChange)
      this.removeListeners()
      this.unloadOrderSounds()
      clearInterval(this.paymentAccountTimer)
      refreshService.unregister(this.refreshAppState)
      refreshService.unregister(this.refreshPaymentAccount)
    },
    methods: {
      onNetworkChange (event) {
        this.$store.commit('network/setOnline', event.type === 'online')
      },
      // init и register идемпотентны: зовём на каждый вход пользователя
      startConnectionRecovery () {
        websocketService.init(this.$store, refreshService)
        // Запасная проба: когда список заказов не открыт, сторож проверяет филиал за сегодня
        refreshService.register(this.refreshAppState, { probe: this.appStateProbe, fallback: true })
        // Счёт опрашивается своим таймером раз в 30 минут - сторожу он не нужен
        refreshService.register(this.refreshPaymentAccount, { watchdog: false })
      },
      // Выход через /logout не перезагружает страницу, а сторож продолжает тикать
      refreshAppState () {
        if (!this.user || !this.user.id || !this.localStorageBranch) {
          return
        }

        return Promise.all([
          this.$store.dispatch('indicators/fetchFilialDayMetrics'),
          this.initStopListBadge(),
          this.loadMissingSoundPresets(),
        ])
      },
      appStateProbe () {
        if (!this.user || !this.user.id || !this.localStorageBranch) {
          return null
        }
        return {
          filialId: this.localStorageBranch.id,
          date: DateTime.local().toFormat('y-MM-dd'),
        }
      },
      refreshPaymentAccount () {
        if (!this.user || !this.user.id) {
          return
        }

        return this.loadPaymentAccount()
      },
      // Вкладку CRM держат открытой сменами: без опроса баланс, блокировка
      // и предупреждение об отключении остаются на экране вчерашними
      startPaymentAccountRefresh () {
        clearInterval(this.paymentAccountTimer)
        this.paymentAccountTimer = setInterval(() => {
          this.loadPaymentAccount()
        }, PAYMENT_ACCOUNT_REFRESH_MS)
      },
      // Бадж стоп-листа в сайдбаре: статус текущего филиала + websocket-подписка.
      // Без права на просмотр стоп-листа не запрашиваем - бадж просто не показывается
      initStopListBadge () {
        if (!this.userCan(CRMPermission.ViewStopList)) {
          return
        }

        this.$store.dispatch('stopList/subscribe')
        return this.$store.dispatch('stopList/fetchStatus')
      },

      // Кому биллинг видно — грузим счёт целиком, остальным только флаг блокировки
      loadPaymentAccount () {
        if (!this.canViewBilling) {
          return this.$store.dispatch('billing/fetchPaymentAccountStatus').catch(() => {})
        }

        return this.$store.dispatch('billing/fetchPaymentAccount', { force: true }).catch(() => {})
      },
      // Флаг из сокета кладём сразу - оверлей реактивен от стора; дальше
      // перечитываем счёт: у собственника карточку, у остальных статус — после
      // разблокировки плашка «истекает» должна обновиться, не дожидаясь опроса
      onBlockingChanged ({ blocked }) {
        this.$store.commit('billing/setPaymentAccount', { blocked: Boolean(blocked) })
        this.loadPaymentAccount()
      },
      updateListeners () {
        // Зовётся из mounted и из deep-watcher'а user: echo на каждый listen добавляет
        // ещё один колбэк, и события (звуки заказов) обрабатывались бы по нескольку раз
        if (this.listenersUserId === this.user.id) {
          return
        }
        this.listenersUserId = this.user.id

        const processCallEvent = (e) => {
          this.callEvent = JSON.parse(JSON.stringify(e))
          this.$store.commit('newOrder/setNewOrderClientId', null)
          this.showButtonOrder = false
          this.showIncomingCall = true
          console.log(
            'Входящий звонок на аккаунт ' +
              e.accountId +
              ' с номера ' +
              e.clientPhone,
          )
        }

        const processAnswerEvent = (e) => {
          this.callEvent = JSON.parse(JSON.stringify(e))
          this.showButtonOrder = true
          this.showIncomingCall = true
          this.$store.commit('newOrder/setCallId', e.callId)
          this.$store.commit('newOrder/setNewOrderClientId', e.clientId)
          this.$store.commit('newOrder/setNewOrderIncomingPhone', e.incomingPhone)
          console.log(
            'Разговор аккаунта ' + e.accountId + ' с номером ' + e.clientPhone,
          )
        }

        const processHangupEvent = (e) => {
          this.showButtonOrder = false
          this.showIncomingCall = false
          this.$store.commit('newOrder/setCallId', null)
          this.$store.commit('newOrder/setNewOrderClientId', null)
          console.log(
            'Разговор аккаунта ' +
              e.accountId +
              ' с номером ' +
              e.clientPhone +
              ' окончен',
          )

          const callEvent = JSON.parse(JSON.stringify(e))

          if (
            callEvent.isCallback &&
            callEvent.status === PhoneCallStatus.Canceled
          ) {
            this.showErrorMessage('Звонок завершен с ошибкой')
          }
        }

        websocketService
          .getEcho()
          .channel('events.user.' + this.user.id)
          .listen('.call_center.incoming_call', processCallEvent)
          .listen('.call_center.answer_call', processAnswerEvent)
          .listen('.call_center.hangup_call', processHangupEvent)
          .listen('User\\CreateTransaction', (res) => {
            this.transactionData = res.data
            this.showIncomingRequest = true
          })

        // websocketService.getEcho()
        //  .channel(`orders.filial.${this.localStorageBranch.id}`)
        //  .listen('Order\\UpdateOrder', this.updatePerformanceIndicators)

        // .listen('Order\\UpdateOrder', this.updateAdminInfo)

        websocketService
          .getEcho()
          .channel(getFilialChannel(this.localStorageBranch.id))
          .listen(
            '.Modules\\Statistic\\Events\\UpdateFilialDayMetricsEvent',
            this.updatePerformanceIndicators,
          )
          .listen(
            'Filial\\ChangeAggregatorIntegrationStatusEvent',
            this.updateAggregatorIntegrationStatus,
          )
          .listen(
            '.statistics.reviews.final_rating.updated',
            this.updateReviewsFinalRating,
          )
          // Блокировка счёта касается всех сотрудников филиала, а на канал
          // счёта у кассира прав нет - флаг приходит сюда
          .listen(CRM_BILLING_BLOCKING_CHANGED, this.onBlockingChanged)
          .listen(PREORDER_AUTO_PREPARED, this.onPreorderAutoPrepared)

        websocketService
          .getEcho()
          .channel(
            getOrdersChannel(this.isCallCenter, this.localStorageBranch.id),
          )
          .listen(UPDATE_ORDER, this.updateOrderListener)

        console.log('Включена прослушка канала входящих звонков')
      },
      updateAggregatorIntegrationStatus (data) {
        if (data?.filialId === this.globalFilialId) {
          this.$store.dispatch(
            'indicators/setAggregatorsIntegrationStatus',
          data?.aggregatorsIntegrationStatus,
          )
        }
      },
      updatePerformanceIndicators: _.debounce(function (params) {
        console.log('updatePerformanceIndicators')
        this.$store.dispatch('indicators/fetchFilialDayMetrics', params)
      }, 3000),
      updateAdminInfo: _.debounce(function () {
        console.log('updateAdminInfo')
        this.$store.dispatch('admin/fetchAdminShortInfo')
      }, 2000),
      updateReviewsFinalRating () {
        console.log('updateReviewsFinalRating')
        this.$store.dispatch('reviews/fetchFinalRating')
      },
      removeListeners () {
        if (this.user) {
          websocketService
            .getEcho()
            .channel('events.user.' + this.user.id)
            .stopListening('.call_center.incoming_call')
            .stopListening('.call_center.answer_call')
            .stopListening('.call_center.hangup_call')
            .stopListening('User\\CreateTransaction')
        }

        websocketService
          .getEcho()
          .channel(
            getOrdersChannel(this.isCallCenter, this.localStorageBranch.id),
          )
          .stopListening(UPDATE_ORDER)

        websocketService
          .getEcho()
          .channel(getFilialChannel(this.localStorageBranch.id))
          .stopListening(CRM_BILLING_BLOCKING_CHANGED)
          .stopListening(PREORDER_AUTO_PREPARED)

        this.listenersUserId = null
      },

      loadGlobalFilialInfo () {},

      setGlobalFilial () {
        console.log({
          globalFilialId: this.globalFilialId,
          allFilials: this.allFilials,
        })

        if (this.globalFilialId) {
          const filialInList = this.allFilials.find(
            (f) => f.id === this.globalFilialId,
          )
          if (!filialInList) {
            this.$store.commit('branches/setCurrentBranch', this.allFilials[0])
            this.$store.commit('appSettings/setGlobalFilial', this.allFilials[0])
            window.location.reload()
          }
        } else if (this.allFilials.length) {
          console.log(this.allFilials.length)
          // Устанавливаем первый филиал из списка
          this.$store.commit('branches/setCurrentBranch', this.allFilials[0])
          this.$store.commit('appSettings/setGlobalFilial', this.allFilials[0])
          window.location.reload()
        }
      /* else {
        this.$store.commit('branches/setCurrentBranch', {})
        this.$store.commit('appSettings/setGlobalFilial', {})
        window.location.reload()
      } */
      },
      // Пресеты не загрузились при открытии вкладки - до конца смены играл бы
      // стандартный звук вместо выбранного, а автоперевод предзаказа молчал бы
      async loadMissingSoundPresets () {
        if (this.soundPresetsLoaded) {
          return
        }

        await this.$store.dispatch('sounds/fetchPresets')
        await this.initOrderSounds()
      },
      async initOrderSounds () {
        const settings = this.globalFilial && this.globalFilial.settings
        if (!this.user || !this.user.id || !settings) {
          return
        }

        let presets = []
        try {
          presets = await this.$store.dispatch('sounds/fetchPresets')
        } catch (e) {
          console.warn('sounds/fetchPresets failed', e)
        }

        this.unloadOrderSounds()

        const newOrderKey = getFilialSettingValue(settings, SettingKey.NewOrderSound)
        this.newOrderSound = this.buildNewOrderSound(this.findSoundUrl(presets, newOrderKey), newOrderKey === NO_SOUND)

        const preorderUrl = this.findSoundUrl(presets, getFilialSettingValue(settings, SettingKey.PreorderAutoPrepareSound))
        this.preorderAutoPrepareSound = preorderUrl ? createSound(preorderUrl) : null
        this.orderSoundsReady = true
      },
      findSoundUrl (presets, key) {
        const preset = presets.find((p) => p.key === key)
        return preset ? preset.url : null
      },
      // Новый заказ нельзя проспать: нет пресета или файл не загрузился - играем звук из CRM
      buildNewOrderSound (url, muted) {
        if (muted) {
          return null
        }

        if (!url) {
          return createSound(FALLBACK_NEW_ORDER_SOUND_URL)
        }

        const sound = createSound(url)
        sound.once('loaderror', () => {
          this.newOrderSound = createSound(FALLBACK_NEW_ORDER_SOUND_URL)
        })
        return sound
      },
      unloadOrderSounds () {
        if (this.newOrderSound) {
          this.newOrderSound.unload()
        }
        if (this.preorderAutoPrepareSound) {
          this.preorderAutoPrepareSound.unload()
        }
        this.newOrderSound = null
        this.preorderAutoPrepareSound = null
        this.orderSoundsReady = false
      },
      onPreorderAutoPrepared () {
        if (this.preorderAutoPrepareSound) {
          this.preorderAutoPrepareSound.play()
        }
      },
      updateOrderListener (data) {
        if (!this.canNewOrderSoundPlay(data.data)) {
          return
        }

        this.announcedNewOrderIds.add(data.data.id)
        this.playNewOrderSound()
      },
      // Пока сокет лежал, новые заказы пришли без звука и появились только при
      // пересинхронизации списка - звучим один раз на всю пачку
      announceMissedNewOrders (shownOrders, freshOrders) {
        const missed = findMissedOrders(shownOrders, freshOrders, this.announcedNewOrderIds)
          .filter(this.canNewOrderSoundPlay)
        if (!missed.length) {
          return
        }

        missed.forEach((order) => this.announcedNewOrderIds.add(order.id))
        this.playNewOrderSound()
      },
      playNewOrderSound () {
        this.ensureFallbackNewOrderSound()
        if (this.newOrderSound) {
          this.newOrderSound.play()
        }
      },
      // Настройки филиала не загрузились - новый заказ всё равно должен прозвучать
      ensureFallbackNewOrderSound () {
        if (!this.orderSoundsReady && !this.newOrderSound) {
          this.newOrderSound = createSound(FALLBACK_NEW_ORDER_SOUND_URL)
        }
      },
      canNewOrderSoundPlay (data) {
        return data.orderStatus === OrderStatus.New &&
          this.userIs(Role.FilialAdmin) &&
          !this.isSuperAdmin &&
          //! data.filial.useCallCenter &&
          data.manager?.id !== this.user.id
      },
    },
  }
</script>

<style lang="less">
body {
  margin: 0;
  // Закрытый temporary-drawer (например, .profile-drawer) уводится за правый край
  // трансформом translateX(100%) и, будучи position:absolute, расширяет ширину
  // документа - появляется горизонтальный скролл. Обрезаем его на body.
  overflow-x: hidden;
}

// Окна оформления заказа на компактном десктопе: у кнопок подтверждения и
// возврата подпись шире самой кнопки («Сохранить пункт самовывоза») - разрешаем
// перенос на вторую строку. Правило общее: блок .button-group с такими кнопками
// есть в полутора десятках окон оформления.
.media-compact-pc({
  .order-processing-page .button-group .button.v-btn {
    // 65px вместо 80px во всех окнах; !important - в части окон высота задана с ним
    height: 65px !important;
    padding: 0 12px;

    // 0 1 auto, не 1 1 auto: содержимое по ширине текста, иконка остаётся рядом с ним
    .v-btn__content {
      flex: 0 1 auto;
      max-width: 100%;
      white-space: normal;
      text-align: center;
      line-height: 1.25;
    }
  }
});

// Полоса над страницей (TopBar: связь или баланс) висит поверх контента, но не
// заходит на боковое меню — опускаем только шапку и содержимое, меню остаётся во
// всю высоту. Класс TopBar ставит на <html>.
.with-top-bar {
  // Выше mobile-breakpoint полоса висит поверх окна, поэтому шапку и контент
  // опускаем сами. Высоту полоса меряет и кладёт в переменную; объявление без
  // var() — запасное для браузеров без CSS-переменных, там она в одну строку.
  // Контент двигаем отступом обёртки, а не .v-main: у него padding-top занулён
  // в layouts/default/View.vue, и шапка наезжала бы на страницу.
  @media (min-width: (@lg + 1)) {
    .v-application--wrap {
      padding-top: 36px;
      padding-top: var(--top-bar-height, 36px);
    }

    // Шапка позиционируется абсолютно, отступ обёртки её не двигает
    .v-app-bar {
      top: 36px !important;
      top: var(--top-bar-height, 36px) !important;
    }
  }

  // На мобильной вёрстке полоса стоит в потоке и место занимает сама, а шапке
  // отступ считает Vuetify — трогать его нельзя, иначе накроет кнопки меню.
  // Но подложка развёрнутой шапки прибита к окну (Header.vue): без сдвига она
  // ложится на кнопки и меню не закрыть. important — scoped-правило шапки
  // специфичнее.
  .media-lg({
    .mobile-wrapper {
      top: 75px !important;
      top: calc(75px + var(--top-bar-height, 0px)) !important;
    }
  });
}
</style>
